import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import type { WorkItem } from "../lib/types";
import { Tile, SectionHeader, Pill, Avatar } from "../components/primitives";
import { priorityVisual, channelChip } from "../lib/status";
import { SlaCountdown } from "../components/SlaCountdown";

// Detail view for a work item (task or ticket). For tickets it foregrounds the SLA countdown.
// Comments + subtasks are live-editable; a status change PATCHes and the realtime feed refreshes
// every other connected client's board/list.
export function ItemDetailView() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const { data: item, isLoading } = useQuery({ queryKey: ["work-item", id], queryFn: () => api.getWorkItem(id) });
  const { data: comments } = useQuery({ queryKey: ["comments", id], queryFn: () => api.listComments(id), enabled: !!id });
  const { data: subtasks } = useQuery({ queryKey: ["subtasks", id], queryFn: () => api.listSubtasks(id), enabled: !!id });
  const [comment, setComment] = useState("");
  const [subtaskTitle, setSubtaskTitle] = useState("");

  const addComment = useMutation({
    mutationFn: () => api.addComment(id, comment),
    onSuccess: () => {
      setComment("");
      void qc.invalidateQueries({ queryKey: ["comments", id] });
    },
  });
  const addSubtask = useMutation({
    mutationFn: () => api.addSubtask(id, subtaskTitle),
    onSuccess: () => {
      setSubtaskTitle("");
      void qc.invalidateQueries({ queryKey: ["subtasks", id] });
    },
  });
  const toggleSubtask = useMutation({
    mutationFn: (v: { sid: string; done: boolean }) => api.updateSubtask(v.sid, v.done),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["subtasks", id] }),
  });

  if (isLoading) return <Tile>Loading…</Tile>;
  if (!item) return <Tile>Not found. <Link to="/board" style={{ color: "var(--gold)" }}>Back to board</Link></Tile>;

  const pv = priorityVisual(item.priority);
  const chip = channelChip(item.source);
  const isTicket = item.kind === "ticket";

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.35fr 1fr", gap: 16, alignItems: "start" }}>
      <Tile>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-4)", letterSpacing: 0.5 }}>{item.ref}</span>
          <Pill className={pv.pillClass}>{pv.label}</Pill>
          {chip && <span className={`chip ${chip.cls}`}>{chip.label}</span>}
          {isTicket && <span style={{ marginLeft: "auto" }}><SlaCountdown item={item} /></span>}
        </div>
        <h2 style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.3px", lineHeight: 1.3 }}>{item.title}</h2>
        {item.description && <p style={{ marginTop: 10, fontSize: 13.5, lineHeight: 1.65, color: "var(--text-2)" }}>{item.description}</p>}

        <StatusChanger item={item} />

        <div style={{ height: 18 }} />
        <SectionHeader title="Comments" />
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
          {comments?.map((c) => (
            <div key={c.id} style={{ display: "flex", gap: 10 }}>
              <Avatar name={c.author_id?.slice(0, 2) ?? "??"} />
              <div>
                <div style={{ fontSize: 13, lineHeight: 1.5 }}>{c.body}</div>
                <div style={{ fontSize: 10.5, color: "var(--text-4)", marginTop: 2 }}>
                  {new Date(c.created_at).toLocaleString()}
                  {c.is_internal ? " · internal" : ""}
                </div>
              </div>
            </div>
          ))}
          {comments && comments.length === 0 && <div style={{ fontSize: 12.5, color: "var(--text-3)" }}>No comments yet.</div>}
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (comment.trim()) addComment.mutate();
          }}
          style={{ display: "flex", gap: 8, marginTop: 12 }}
        >
          <input
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add a comment…"
            aria-label="Add a comment"
            style={{ flex: 1, background: "var(--bg-2)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", padding: "8px 12px", fontSize: 13, color: "var(--text-1)", fontFamily: "inherit" }}
          />
          <button className="btn-gold" style={{ padding: "8px 14px", cursor: "pointer" }} disabled={addComment.isPending}>
            Send
          </button>
        </form>
      </Tile>

      <Tile>
        <SectionHeader title="Subtasks" />
        <div style={{ display: "flex", flexDirection: "column", gap: 1, marginTop: 10 }}>
          {subtasks?.map((s) => {
            const done = Boolean(s.completed_at);
            return (
              <label key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 4px", borderBottom: "1px solid var(--border)", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={done}
                  onChange={() => toggleSubtask.mutate({ sid: s.id, done: !done })}
                  style={{ accentColor: "var(--gold)", width: 15, height: 15 }}
                />
                <span style={{ fontSize: 13, color: done ? "var(--text-4)" : "var(--text-1)", textDecoration: done ? "line-through" : "none" }}>
                  {s.title}
                </span>
              </label>
            );
          })}
          {subtasks && subtasks.length === 0 && <div style={{ fontSize: 12.5, color: "var(--text-3)", padding: "8px 4px" }}>No subtasks.</div>}
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (subtaskTitle.trim()) addSubtask.mutate();
          }}
          style={{ display: "flex", gap: 8, marginTop: 12 }}
        >
          <input
            value={subtaskTitle}
            onChange={(e) => setSubtaskTitle(e.target.value)}
            placeholder="Add a subtask…"
            aria-label="Add a subtask"
            style={{ flex: 1, background: "var(--bg-2)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", padding: "8px 12px", fontSize: 13, color: "var(--text-1)", fontFamily: "inherit" }}
          />
          <button className="btn-gold" style={{ padding: "8px 14px", cursor: "pointer" }} disabled={addSubtask.isPending}>
            Add
          </button>
        </form>

        <div style={{ height: 16 }} />
        <SectionHeader title="Details" />
        <dl style={{ marginTop: 10, fontSize: 12.5, display: "grid", gridTemplateColumns: "auto 1fr", gap: "8px 14px" }}>
          <dt style={{ color: "var(--text-3)" }}>Kind</dt>
          <dd style={{ textTransform: "capitalize" }}>{item.kind}</dd>
          <dt style={{ color: "var(--text-3)" }}>Status</dt>
          <dd>{item.status}</dd>
          <dt style={{ color: "var(--text-3)" }}>Created</dt>
          <dd>{new Date(item.created_at).toLocaleString()}</dd>
          {item.due_at && (
            <>
              <dt style={{ color: "var(--text-3)" }}>Due</dt>
              <dd>{new Date(item.due_at).toLocaleString()}</dd>
            </>
          )}
        </dl>
      </Tile>
    </div>
  );
}

function StatusChanger({ item }: { item: WorkItem }) {
  const qc = useQueryClient();
  const { data: board } = useQuery({ queryKey: ["board", item.kind], queryFn: () => api.board(item.kind) });
  const patch = useMutation({
    mutationFn: (status: string) => api.updateWorkItem(item.id, { status }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["work-item", item.id] });
      void qc.invalidateQueries({ queryKey: ["board", item.kind] });
    },
  });
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
      <span style={{ fontSize: 11.5, color: "var(--text-3)", fontWeight: 600 }}>Status:</span>
      {board?.columns.map((c) => (
        <button
          key={c.status}
          onClick={() => patch.mutate(c.status)}
          className={`nav ${item.status === c.status ? "on" : ""}`}
          style={{ padding: "5px 11px", fontSize: 12 }}
          aria-pressed={item.status === c.status}
        >
          {c.label}
        </button>
      ))}
    </div>
  );
}
